/**
 * The day's stops as the packet names them. The stops are numbered 1, 2, 3…
 * in route order on the cover and keep their number on every later
 * page, "so a loose page can always be sorted back" (Matt, Notes · A1). Matt
 * lettered them; Abner numbered them (Oct 18: "Rename stops from letters (A,
 * B, C) to numbers (1, 2, 3)").
 *
 * Route order is the Delivery route's (`sheetsInRouteOrder`): the driven
 * stops in order, then dry runs, then any school the route does not know.
 * Pure; unit-tested.
 */

import type { DeliveryRouteDay, DeliveryRouteStop } from '@/api/delivery-route'
import { deliverableMode } from '@/lib/chefs/deliverable-copy'
import { clockLabel, mealStartsLine, phoneLink, windowLabel } from '@/lib/chefs/delivery-route'
import { sheetsInRouteOrder } from '@/lib/chefs/print-words'
import type { Campus } from '@/lib/chefs/types'
import { dryRunServiceTypeLabel } from '@/lib/chefs/types'

import type { StopIdentity, StopType } from './units'

/** 0 → "1", 11 → "12". */
export function stopNumber(index: number): string {
  return String(Math.max(0, Math.floor(index)) + 1)
}

/** The organization when it names something other than the site ("OLHSA" over "Pontiac Main Building"). */
export function stopOrganization(campus: Pick<Campus, 'name' | 'organizationName'>): string | null {
  const organization = campus.organizationName.trim()
  return organization && organization.toLowerCase() !== campus.name.trim().toLowerCase() ? organization : null
}

/**
 * BULK when the stop's food rides in shared vessels (pans, boxes), UNITIZED
 * when it rides one container per student, MIXED when it takes both. Loose
 * items (a juice in the delivery box) are counted, so they read as unitized.
 * Milk has its own rows and does not decide it. Null when the stop has no food.
 */
export function stopType(campus: Pick<Campus, 'mealtimes'>): StopType | null {
  let bulk = false
  let unitized = false
  for (const block of campus.mealtimes) {
    for (const d of block.deliverables) {
      if (deliverableMode(d) === 'bulk') {
        bulk = true
      } else {
        unitized = true
      }
    }
  }
  if (bulk && unitized) {
    return 'MIXED'
  }
  return bulk ? 'BULK' : unitized ? 'UNITIZED' : null
}

export interface PacketStop {
  identity: StopIdentity
  campus: Campus
  /** The route's stop; null when the route is unknown or does not drive here. */
  route: DeliveryRouteStop | null
  /** "7:00 to 9:00 AM" (the route's), else the school's own words; null when unknown. */
  window: string | null
  /** "Lunch at 11:00 AM", plus "drop 1st" when two stops share a window. */
  windowNote: string | null
  /** "196 Cesar E Chavez, Pontiac, MI 48342". */
  address: string | null
  /** "Danielle Kelley · (248) 897-4807". */
  contact: string | null
  /** The contact's name alone, "Danielle Kelley", for the delivery sheet's CONTACT column. */
  contactName: string | null
  /** The contact's phone alone, "(248) 897-4807". */
  contactPhone: string | null
  /** The same person phone first, as the route's phone line reads: "(248) 897-4807 · Danielle Kelley". */
  phoneLine: string | null
  /** "Dry run: do not deliver" / "Tasting: deliver as live service". */
  warning: string | null
}

function campusAddress(campus: Campus): string | null {
  const info = campus.info
  const street = [info.addressLine1, info.addressLine2].map((part) => part?.trim()).filter(Boolean)
  const cityLine = [info.city?.trim(), [info.state?.trim(), info.zip?.trim()].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')
  const text = [...street, cityLine].filter(Boolean).join(', ')
  return text || null
}

/** The stop's contact person and phone: the route's, else the school's own. */
function contactParts(campus: Campus, stop: DeliveryRouteStop | null): { name: string | null; phone: string | null } {
  if (stop) {
    const name = stop.contactName?.trim() || null
    const phone = phoneLink(stop.contactPhone)?.label ?? null
    if (name || phone) {
      return { name, phone }
    }
  }
  return {
    name: campus.info.pocName.trim() || null,
    phone: phoneLink(campus.info.phone)?.label ?? (campus.info.phone.trim() || null),
  }
}

function joined(parts: ReadonlyArray<string | null>): string | null {
  const present = parts.filter((part): part is string => Boolean(part))
  return present.length > 0 ? present.join(' · ') : null
}

function lunchNote(campus: Campus, stop: DeliveryRouteStop | null): string | null {
  if (stop) {
    return mealStartsLine(stop)
  }
  const raw = campus.info.lunchStart.trim()
  return raw ? `Lunch at ${clockLabel(raw) ?? raw}` : null
}

function ordinal(n: number): string {
  const rem100 = n % 100
  if (rem100 >= 11 && rem100 <= 13) {
    return `${n}th`
  }
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`
}

/**
 * "drop 1st" / "drop 2nd" for consecutive driven stops that share one window
 * (Huda's two campuses, 10:45 to 11:15): the window alone does not say which
 * door comes first.
 */
export function dropOrders(stops: ReadonlyArray<Pick<DeliveryRouteStop, 'window'>>): Array<string | null> {
  const key = (stop: Pick<DeliveryRouteStop, 'window'>) =>
    stop.window && (stop.window.start || stop.window.end) ? `${stop.window.start ?? ''}|${stop.window.end ?? ''}` : null
  const out: Array<string | null> = stops.map(() => null)
  let i = 0
  while (i < stops.length) {
    const k = key(stops[i]!)
    let j = i + 1
    while (k !== null && j < stops.length && key(stops[j]!) === k) {
      j += 1
    }
    if (j - i > 1) {
      for (let n = i; n < j; n += 1) {
        out[n] = `drop ${ordinal(n - i + 1)}`
      }
    }
    i = j
  }
  return out
}

/** The stop's dry-run line, on every stop page; a tasting IS delivered. */
export function stopWarning(campus: Pick<Campus, 'isDryRun' | 'dryRunServiceType'>): string | null {
  if (!campus.isDryRun) {
    return null
  }
  return campus.dryRunServiceType === 'TASTING'
    ? 'Tasting: deliver as live service'
    : `${dryRunServiceTypeLabel(campus.dryRunServiceType)}: do not deliver`
}

/** The day's stops, numbered in route order, with what every stop page says about them. */
export function packetStops(campuses: readonly Campus[], routeDay: DeliveryRouteDay | null): PacketStop[] {
  const stopsById = new Map<string, DeliveryRouteStop>()
  for (const stop of [...(routeDay?.stops ?? []), ...(routeDay?.notDriven ?? [])]) {
    stopsById.set(stop.schoolLocationId, stop)
  }
  const drops = new Map<string, string | null>()
  const driven = routeDay?.stops ?? []
  dropOrders(driven).forEach((drop, i) => drops.set(driven[i]!.schoolLocationId, drop))

  return sheetsInRouteOrder(campuses, routeDay).map(({ campus }, i) => {
    const route = stopsById.get(campus.id) ?? null
    const organization = stopOrganization(campus)
    const window = (route ? windowLabel(route.window) : null) ?? (campus.info.deliveryWindow.trim() || null)
    const person = contactParts(campus, route)
    return {
      identity: {
        number: stopNumber(i),
        organization,
        site: campus.name,
        label: organization ? `${organization} · ${campus.name}` : campus.name,
        type: stopType(campus),
      },
      campus,
      route,
      window,
      windowNote: joined([lunchNote(campus, route), drops.get(campus.id) ?? null]),
      address: route?.address?.text ?? campusAddress(campus),
      contact: joined([person.name, person.phone]),
      contactName: person.name,
      contactPhone: person.phone,
      phoneLine: joined([person.phone, person.name]),
      warning: stopWarning(campus),
    }
  })
}
